import {
  CaseId,
  FolderId,
  TDOId,
  TreeObjectId,
} from '@common-modules/universal/models/Brands';
import { FolderPathItem } from '@redact-modules/helper';
import { SYSTEM_FOLDER_REDACT_TRASH } from '@redact-modules/trash/constants';
import { type RedactionStatusLabel } from '@common-modules/mediaDetails/redactionStatuses';
import type { JobList, JobStatus, Maybe } from 'veritone-types';

export const namespace = 'vtn-redact-state-casemedia';

/**
 * VE-30022 — one trip away from the folder view (see
 * `CaseMediaState['folderViewTrip']`).
 */
export interface FolderViewTrip {
  /** When the folder view was left for an editor. */
  leftAt: number;
  /**
   * The folders whose columns were on screen at that moment — the selected
   * chain plus the open request. Only these may be put back on return.
   */
  folderIds: string[];
}

export interface CaseMediaState {
  rootFolderId: null | string;
  rootFolder?: null | FolderStateType;
  selectedFolders: FolderStateType[];
  selectedVirtualFolder: VirtualFolderStateType;
  defaultFetchMoreLimit: number;
  defaultFetchMoreOffset: number;
  deletingFileInProgress: boolean;
  fetchingRootFolders: boolean;
  isNavToCaseMedia: boolean;
  navToId?: FolderId;
  isNavToCase: boolean;
  // VE-25873 Unit 3 — the parent request resolved when an editor session
  // opened a file (set at editor route init). CaseFolders consumes it once on
  // mount, as the top branch of its selection-priority effect, so exiting the
  // editor lands on this request. Null when no editor session is pending.
  editorReturnTarget?: { folderId: FolderId; isCase: boolean } | null;
  /**
   * VE-30022 — the folder view's current trip away from the screen: when it was
   * left for an editor, and which columns it was showing at that moment.
   *
   * Stamped at editor route init (`resolveEditorReturnTarget`) when arriving
   * from a non-editor route, and left in place until the next such arrival, so
   * the return path can ask the only question that matters for reuse — "how
   * long have I been away, and was this column one of mine?" — without
   * consulting when each list happened to load. An editor-to-editor hop (the
   * case-media drawer, the document switcher, a trim or view-redacted
   * redirect) deliberately does NOT restart it: the user has not been back to
   * the folder view, so the trip has not ended.
   *
   * `folderIds` is what keeps the window from over-reaching. Rows survive in
   * `caseContentsRows` for the whole session, so an unscoped time check would
   * licence reusing ANY previously visited request's list for a minute after
   * ANY editor entry; scoped to the columns that were actually on screen, it
   * can only ever put back the view the user just left.
   */
  folderViewTrip: FolderViewTrip | null;
  searchText: string;
  searchDefaultOffset: number;
  searchDefaultFolderLimit: number;
  searchDefaultFileLimit: number;
  searchMoreFilesOffset: number;
  searchMoreFoldersOffset: number;
  searchMoreFilesAvailable: boolean;
  searchMoreFoldersAvailable: boolean;
  isSearchMoreResults: boolean;
  isSearchInProgress: boolean;
  searchResults: (SearchResultFolder | SearchResultFile)[];
  // Cached results per (query + scope mode + scope folder), so toggling the
  // scope tabs restores prior results instead of re-running the search.
  // Cleared whenever the query text changes.
  searchResultsCache: Record<string, SearchResultsCacheEntry>;
  searchScopeFolderId: FolderId | null;
  searchScopeTreeObjectId: TreeObjectId | null;
  searchScopeFolderName: string | null;
  searchScopeIsCase: boolean;
  searchBarScopeMode: 'selectedFolder' | 'everything';
  isSearchDialogOpen: boolean;
  isShowLegacyMedias: boolean;
  isFetchingLegacyMedias: boolean;
  LegacyMedias: LegacyMediaDetailType[];
  /**
   * VE-26943: the enriched TDO behind each unassigned SDO, keyed `id_<tdoId>` as
   * the aliased batch query returns it. Held separately from `LegacyMedias`
   * (which is the SDO side) so the synthetic Unassigned Media folder can be
   * DERIVED from the two — see selectUnassignedFolder — rather than maintained
   * as a third copy of the same list.
   */
  legacyTdoDetails: Record<string, UnassignedTdoRecord>;
  /**
   * VE-26943: has the mount probe for the unassigned list come back yet (either
   * way)? The landing rule -- an org with no folders opens Unassigned Media --
   * has to distinguish "no unassigned files" from "not asked yet", or it would
   * decide before the answer arrives.
   */
  hasFetchedLegacyMedias: boolean;
  /**
   * VE-26943: which unassigned TDOs the enrichment has been ATTEMPTED for,
   * keyed `id_<tdoId>`. Rows are withheld until their detail arrives, so that a
   * raw tdoId is never shown as a filename — but a file whose enrichment failed
   * would then be hidden forever, and the SDOs are the record that it exists.
   * Marking attempts (on success AND failure) releases exactly those rows,
   * degraded, and leaves everything else withheld.
   *
   * This replaced a single `legacyTdoDetailsFailed` boolean, which could not say
   * WHICH rows were affected: it was cleared by the next page's fetch, so
   * scrolling re-hid rows an earlier failure had released, and it made the
   * loading spinner stop on the FIRST of several parallel batches.
   */
  legacyTdoDetailAttempted: Record<string, true>;
  fetchMoreLegacyMediasOffset: number;
  fetchMoreLegacyMediasAvailable: boolean;
  sourceFolderToMove?: FolderStateType;
  moveFromFolder?: FolderStateType | null;
  allExpandedFolders: FolderStateType[];
  sourceFileToMove?: LegacyMediaDetailType;
  selectedRequestToIngest?: CaseId;
  isIlluminateAppEnabled?: boolean;
  // Mirror of the `rootFolderSmartSelection` app-config flag. The root-folder
  // reducers resolve which of the returned roots to use, and reducers can't
  // read the app-config slice — the fetch sagas write the current flag value
  // here right before each `rootFolders` fetch.
  rootFolderSmartSelection?: boolean;
  sourceInputFileToMove?: AssetFileType;
  filters: FileFilters;
  // How a case's input files are rendered — classic rows or a thumbnail-tile
  // grid (the shared MediaTile). Persisted to localStorage (last-used mode
  // wins on reload); the Header toggle writes it via persistFileViewMode.
  fileViewMode: FileViewMode;
  /** VE-26943: Unassigned Media's own view mode (Grid by default). */
  unassignedFileViewMode: FileViewMode;
  /**
   * The order of the unassigned SDO list (VE-32420). Separate from
   * `filters.sort` because that one is a client pass over loaded rows, and this
   * is the SERVER's ordering, applied by the query.
   *
   * GOVERNS BOTH SURFACES THAT SHOW THAT LIST — the Unassigned Media column and
   * the docked panel — because there is one list in state and one query behind
   * it. The dock has no sort control of its own, which is why leaving the
   * column puts this back to the default (`resetUnassignedSortOnLeave`): the
   * dock is a glance, and it should not be ordered by something chosen on a
   * screen the user has left. So the value below is what the dock always shows.
   *
   * Lives only as long as the column is open, which is shorter than `filters`
   * and shorter still than the view modes, which persist.
   */
  unassignedSort: UnassignedSort;
  /** VE-26943: folder sidebar collapsed while Unassigned Media is open. */
  isUnassignedSidebarCollapsed: boolean;
  /** VE-26943: folder sidebar collapsed for any other open request. */
  isPanelSidebarCollapsed: boolean;
  // Lifecycle of the fetchCaseOrFolderTree call (deep links + last-open
  // restore). CaseFolders keeps a loading state up instead of flashing the
  // empty column view while a restore is pending.
  folderTreeFetchState: 'idle' | 'fetching' | 'succeeded' | 'failed';
  /**
   * VE-28429: when each case's contents last landed, keyed by the id the
   * contents were fetched under, as `Date.now()`.
   *
   * Only the case-contents read stamps here — folder listings have their own
   * `subFoldersLoadedAt`. Entries are never evicted — one number per case
   * visited this session.
   *
   * VE-30022: this is now a RECORD, not a gate. It used to bound the editor
   * return's reuse, which measured the wrong thing (see
   * `FOLDER_VIEW_AWAY_REUSE_MS` in ../contants); that decision moved to
   * `folderViewTrip`. What is left is the honest answer to "when did this list
   * actually come off the server", kept deliberately so a background refresh —
   * the thing that should decide a list is too old while the user is sitting in
   * front of it — has a number to run from.
   */
  caseContentsLoadedAt: Record<string, number>;
  /**
   * VE-28429: the last loaded file list per request, kept so a re-select can put
   * it straight back on screen.
   *
   * `selectedFolders` cannot be relied on for this: restoring the folder tree
   * re-selects each node as a bare tree node, and `mapSelectedFolders` replaces
   * the whole chain when the incoming node has no `folderPath` — so the rows are
   * already gone by the time the request itself is re-selected. Keyed by folder
   * id; `folderViewTrip` bounds how long they may be used.
   */
  caseContentsRows: Record<string, CaseFilesType>;
  /**
   * The folder-listing counterpart of `caseContentsLoadedAt`: when each folder's
   * first page of subfolders last landed. A record for the same reason — the
   * column one level out from a request has its own list, loaded at its own
   * time, and a background refresh will need to know when.
   */
  subFoldersLoadedAt: Record<string, number>;
  /**
   * The listing itself, kept for the same reason as `caseContentsRows`: the tree
   * restore trims `selectedFolders` as it re-selects each ancestor, so a
   * deeper ancestor's listing is gone before its turn comes.
   */
  subFoldersRows: Record<string, RetainedSubFolderListing>;
  /**
   * VE-30449: every file name in a request, for the upload duplicate-name
   * check, keyed by request (folder) id. Fetched when the upload dialog opens
   * and dropped when it closes, so it is a snapshot as of that open — see
   * `RequestRosterState`.
   */
  requestRoster: Record<string, RequestRosterState>;
  lastActiveFolderViewFile: LastActiveFolderViewFile | null;
  openedFolderViewTdoIds: TDOId[];
  // Last file actually OPENED (navigation) per case, keyed by caseId. Drives the
  // "Last Opened" badge and the return-scroll target. Session-long and NOT
  // cleared on case switch (unlike `lastActiveFolderViewFile`), so the badge
  // survives navigating into another case and back.
  lastOpenedFolderViewFileByCase: Record<string, TDOId>;
  // OLP verdicts per folder, one entry per folder a check has CONFIRMED. Absent
  // = not confirmed (never checked / in flight / the check failed); the
  // selectors read that as "not permitted". See FolderPermissionFlags for why
  // the three aiWARE permissions are tracked separately.
  folderPermissions: Record<FolderId, FolderPermissionFlags>;
  // VE-25098: multi-file selection in the folder view. Scoped to the input
  // files of a single case (`multiSelectCaseId`) — selecting a file in another
  // case resets the selection to that case. `multiSelectAnchorId` is the pivot
  // for Shift+Click range selection. Cleared on folder navigation and after a
  // batch move/trash completes.
  multiSelectedFiles: MultiSelectedFile[];
  multiSelectCaseId: CaseId | null;
  multiSelectAnchorId: TDOId | null;
  // VE-25098: input-file TDO ids optimistically hidden from the list while a
  // batch trash/move runs in the background. Rows are hidden the instant the
  // action is confirmed and released again if their operation fails.
  pendingRemovalFileIds: TDOId[];
  // VE-26484: folders whose move into Trash is confirmed but still in flight.
  // Until the relocation commits, the server keeps returning the folder under
  // its old parent (now wearing the trash-SDO link, i.e. a briefcase icon), so
  // ANY root refetch landing in this window resurrects it. mapRootFolder filters
  // these ids out; the trash saga releases them once the move settles.
  pendingTrashFolderIds: TreeObjectId[];
  // VE-25098: in-flight batch operation (trash/move/status). Keeps the
  // selection bar visible with a spinner + remaining count while the work runs
  // in the background, and gates the beforeunload guard. Null when idle.
  batchOperation: BatchOperationState | null;
}

export interface MultiSelectedFile {
  id: TDOId;
  name: string;
}

export interface BatchOperationState {
  kind: 'trash' | 'move' | 'status' | 'watermark';
  caseId: CaseId | null;
  total: number;
  remaining: number;
}

// Object-level (OLP) verdicts for one folder. The three aiWARE permissions are
// tracked separately because the folder-view actions do NOT map onto a single
// one of them, and gating them all on UPDATE mis-gates in both directions:
//   canEdit   AIWARE_FOLDER_UPDATE — `updateFolder` (rename).
//   canMove   AIWARE_FOLDER_FILE   — `moveFolder`. A soft delete performs one of
//             these too, since moving to Trash is a moveFolder rather than a
//             deleteFolder (see softDeleteFolder in ../services).
//   canDelete AIWARE_FOLDER_DELETE — `deleteFolder`. Required by BOTH delete
//             branches: a soft delete only defers the purge to retention expiry,
//             so it demands canDelete AND canMove; a hard delete
//             (trashRetentionDays === 0) needs canDelete alone.
export interface FolderPermissionFlags {
  readonly canEdit: boolean;
  readonly canMove: boolean;
  readonly canDelete: boolean;
}

export interface LastActiveFolderViewFile {
  caseId: CaseId;
  // Last file the user interacted with — via an open OR an in-place action
  // (kebab / status / download / review). Drives the blue row highlight. Reset
  // on case switch / back-nav.
  tdoId: TDOId;
  isReturnScrollPending: boolean;
}

export interface LegacyMediaSdoType {
  records?: LegacyMediaType[];
}
export interface LegacyMediaFileDataType {
  tdoId: TDOId;
  status: RedactionStatusLabel;
  toBeDeletedTime?: string;
}
export interface SoftDeletedMediaFileDataType extends LegacyMediaFileDataType {
  oldFolderPath?: FolderPathItem[];
}
export interface LegacyMediaType {
  id: string;
  createdDateTime: string;
  modifiedDateTime: string;
  data: LegacyMediaDetailType;
}
/**
 * VE-26943: one TDO as returned by `getUnassignedTdoDetailsQuery` — the same
 * per-file selection set `fetchCaseContentsQuery` pulls for a request's files,
 * which is what lets `mapUnassignedFile` produce an ordinary `AssetFileType`.
 *
 * Loosely typed on purpose: it mirrors a GraphQL payload whose asset
 * sub-selections are re-narrowed by `AssetFileType` downstream, and `details` is
 * schemaless JSON on the API.
 */
export interface UnassignedTdoRecord {
  id: TDOId;
  name?: string;
  status?: string;
  thumbnailUrl?: string;
  createdDateTime?: string;
  modifiedDateTime?: string;
  startDateTime?: string;
  stopDateTime?: string;
  details?: Record<string, unknown>;
  folders?: unknown[];
  tasks?: unknown;
  jobs?: unknown;
  primaryAsset?: unknown;
  streams?: unknown;
  redactedMediaAssets?: unknown;
  auditLogAssets?: unknown;
  redactedTranscriptAssets?: unknown;
  redactionSelectionAssets?: unknown;
  redactionDataAssets?: unknown;
}

export interface LegacyMediaDetailType {
  sdoId: string;
  /**
   * Canonical status identity written alongside `status` by
   * getSchemaCompatibleUpsertData. Needed to resolve a CUSTOM status, which is
   * stored by key rather than by display label (VE-24546).
   */
  statusKey?: string;
  tdoId: TDOId;
  tdoName: string;
  parentFolderId: CaseId;
  signedUri: string;
  fileType: string;
  status: RedactionStatusLabel;
  jobs: Maybe<JobList>;
  createdDateTime?: string;
  modifiedDateTime?: string;
  details?: {
    tags?: Record<string, string>[];
  };
}

export interface FolderStateType<ID = FolderId> {
  id?: ID;
  name?: string;
  treeObjectId?: TreeObjectId;
  subfolders?: FolderStateType[];
  count?: number;
  ownerId?: null | string;
  caseFiles?: CaseFilesType | null;
  exportedTdo?: ExportedTdoState | null;
  folderPath?: FolderStateType[];
  level?: number;
  createdDateTime?: string;
  childTDOs?: {
    records: AssetFileType[];
  };
  childFolders?: {
    count?: number;
    records: FolderStateType[];
  };
  parent?: {
    id: FolderId;
  };
  contentTemplates?: ContentTemplateType[] | [];
  fetchSubFolderLimit?: number;
  fetchSubFolderOffset?: number;
  fetchingSubFolders?: boolean;
  fetchMoreAvailable?: boolean;
  deletingFolderInProgress?: boolean;
  fetchForExpandInprogress?: boolean;
}

/**
 * One file in a request's roster — everything the folder view can decide
 * WITHOUT the heavy per-row query (VE-32420).
 *
 * Deliberately not an `AssetFileType`: this is the cheap index, and anything
 * added here is paid for across the whole request rather than 30 rows. A
 * field belongs here only if sorting, filtering, selection or a bulk WRITE
 * needs it for a file the user may never have scrolled to.
 */
export interface RequestRosterEntry {
  id: TDOId;
  /** Raw TDO name; compare with `fileNameKey`, not `===`. */
  name: string;
  /**
   * The WHOLE `details` blob, not a subset. The status and trash writes spread
   * it back onto the TDO, so a trimmed copy silently drops keys server-side.
   *
   * Modelled exactly as a loaded row models it — same blob, same TDO — so one
   * predicate can read either. The QUERY's type stays loose, because that is
   * the wire; the conversion happens once, at the service boundary.
   */
  details?: AssetFileType['details'];
  createdDateTime?: string | null;
  modifiedDateTime?: string | null;
  /** Media running time, for the Details-mode Duration column. */
  startDateTime?: string | null;
  stopDateTime?: string | null;
  /**
   * The TDO's ingestion state. Without it `getFileProcessingStatus` falls
   * through to its `default: 'error'` branch and a roster row claims an
   * ingestion failure it knows nothing about.
   */
  status?: string | null;
  /**
   * Whether a redacted output asset exists RIGHT NOW — the redaction sort and
   * the redacted / not-redacted filter. Resolved from an existence check at
   * read time rather than from `details`, because nothing in `details` tracks
   * it: `latestRedactedDocumentVersion` is a high-water mark that survives the
   * asset being deleted, and `redactedMediaAssetId` lives on the audit-log
   * asset rather than the TDO.
   */
  hasRedactedOutput: boolean;
}

/**
 * One request's roster: every file in it, cheaply (VE-32420).
 *
 * Consistency: a snapshot as of when the request was opened. A file another
 * user adds after that is not in it. That is tolerable for sorting, filtering
 * and counts, and the upload duplicate-name check still catches collisions
 * within a single upload batch regardless.
 *
 * `entries` is appended page by page while `status` is `loading`, so a reader
 * that cannot wait for the whole request still sees everything read so far.
 */
export interface RequestRosterState {
  status: 'loading' | 'ready' | 'error';
  entries: RequestRosterEntry[];
  /** Correlates a response with the fetch that asked for it. */
  requestId: string;
}

export type RetainedSubFolderListing = Pick<
  FolderStateType,
  | 'subfolders'
  | 'fetchSubFolderLimit'
  | 'fetchSubFolderOffset'
  | 'fetchMoreAvailable'
>;

export interface CaseFilesType {
  inputFiles?: AssetFileType[];
  outputFiles?: AssetFileType[];
}
export interface FolderPathType {
  id: FolderId;
  name: string;
  treeObjectId: TreeObjectId;
}
export interface ContentTemplateType {
  id: string;
  folderId?: FolderId;
  sdoId?: string;
  schemaId: string;
  // Null when the referenced SDO has been deleted out from under the link: the
  // API answers with a `not_found` error for this field and keeps the rest of
  // the record (returnPartialDataOnNotFound). A null `sdo` means "the SDO is
  // already gone" — the link is a dangling orphan (VE-26016), not a case
  // without data. Mirrors TrashContentTemplate.sdo.
  // the case-record fields below are OPTIONAL because a folder can
  // carry templates of several schemas and this one type describes all of
  // them. A projects template (`projects`) has none of the case fields, and a
  // reader that wants the case record must select by `schemaId` first rather
  // than assuming every template is one.
  sdo?: {
    data?: {
      caseName?: string;
      status?: string;
      folderTreeObjectId?: string;
      createdDateTime?: string;
      modifiedDateTime?: string;
      /** the request's document projects; see `projects/models`. */
      projects?: unknown;
      // Stamped at creation by createNewFolderSdoQuery; carried through the
      // soft-delete spread. Nothing in redact-app reads either.
      priority?: number;
      processingTime?: number;
      isCase?: boolean;
      oldFolderPath?: FolderPathItem[];
      trashRetentionDays?: number;
      /** Legacy stamp on items trashed before the trashRetentionDays rename. */
      numberOfDaysToKeep?: number;
      movedToTrashDateTime?: string;
    };
  } | null;
}
export interface VirtualFolderStateType {
  parent?: FolderStateType<CaseId>;
  virtualFolderName?: VirtualFolderNames;
  fetchingFiles?: boolean;
  deletingFileInProgress?: boolean;
}
export interface ExportedTdoState {
  id?: string;
  assets?: AssetsType;
  downloadAllStarted?: boolean;
  parentFolderId?: string;
  redactedExportAssets?: {
    records: AssetFileType[];
  };
}
export interface AssetsType {
  records: AssetFileType[];
}
interface recordType {
  status: JobStatus;
}

export interface AssetFileType {
  id: TDOId;
  /**
   * VE-32420: the roster's verdict on whether a redacted output exists, set
   * only on rows that came from the roster rather than from
   * `fetchCaseContentsQuery`. Read it through `fileHasRedactedOutput`, never
   * directly — a loaded row answers from its own asset list instead, and the
   * two must not be mixed up. See `redactedOutput`.
   */
  hasRedactedOutput?: boolean;
  /**
   * VE-26943: present only on Unassigned Media files, where it is the id of the
   * redact-state SDO whose existence marks the file as unassigned. Its presence
   * is the "this file is unassigned" test for the paths that must behave
   * differently: delete (SDO delete), move (fileTdo + SDO delete) and drag
   * source tagging.
   */
  sdoId?: string;
  name: string;
  contentType: string;
  assetType: string;
  createdDateTime: string;
  modifiedDateTime: string;
  startDateTime?: string;
  stopDateTime?: string;
  // Fetched by fetchCaseContentsQuery; used by the thumbnail view mode.
  thumbnailUrl?: string;
  signedUri: string;
  /**
   * VE-26943: the TDO this asset belongs to, stamped when outputs are flattened
   * off their file. Output assets are collected into one flat list that loses
   * the parent link, and the code used to recover it by string surgery on the
   * id (`id.split('_')[0]`, `id.startsWith(tdoId)`) — the `<tdoId>_<uuid>` shape
   * is an aiWARE convention, not a guarantee, and it already caused a
   * near-miss where "tdo1" could claim "tdo12"'s assets (see
   * buildOwningFileResolver). Carry the id instead of parsing it.
   */
  parentTdoId?: TDOId;
  /**
   * VE-25870: byte size of an output asset, shown as File Size in Download Review.
   * Only fetched for redacted-media assets, and nullable on the server, so absent
   * means unknown rather than empty.
   */
  fileData?: {
    size?: number | null;
  };
  tasks?: {
    records?: Array<{
      engineId: string;
      status: string;
      /** speaker separation is matched by category, not by a single engine id */
      engine?: { categoryId?: string };
    }>;
  };
  jobs?: {
    count?: number;
    records?: recordType[];
  };
  status?: string;
  veritoneFile: {
    fileType: string;
    mimetype?: string;
  };
  downloadAllStarted?: boolean;
  parentFolderId?: string;
  primaryAsset?: {
    contentType: string;
    signedUri?: string;
    createdDateTime?: string;
  };
  redactedMediaAssets?: {
    records: AssetFileType[];
  };
  auditLogAssets?: {
    records: AssetFileType[];
  };
  /** VE-25870: `redacted-transcript` outputs, offered as their own option. */
  redactedTranscriptAssets?: {
    records: AssetFileType[];
  };
  /**
   * The newest `redaction-user-selections` asset, details only. A trimmed output
   * has no version of its own and borrows the one recorded here (VE-25870).
   */
  redactionSelectionAssets?: {
    records: Array<{ id: string; details?: Record<string, unknown> }>;
  };
  /**
   * Recent redaction saves (counts only, no bodies) — lets a row tell whether its
   * redacted output still matches the saved redactions (VE-25870).
   */
  redactionDataAssets?: {
    records: Array<{
      id: string;
      createdDateTime: string;
      details?: Record<string, unknown>;
    }>;
  };
  details?: {
    tags?: (Record<string, string | object> | null)[];
    tdos?:
      | null
      | {
          [id: TDOId]: {
            auditLogAssetId?: null | string;
            auditLogName?: null | string;
            redactedMediaAssetId?: null | string;
            redactedMediaName?: null | string;
          };
        }[];
    isExport?: boolean;
    /**
     * this TDO is a document *project* (the holder of a combined
     * redacted output), not a source file. Flat beside `project` rather than
     * inside it because `mapMediaFiles` tests it on every row of every request.
     */
    isProject?: boolean;
    /**
     * The TDO's own name, duplicated here by every rename path. No row reader
     * uses it — the folder view's status change and soft delete write the
     * row's whole `details` back, which is what keeps it worth maintaining.
     */
    name?: string;
    veritoneFile?: {
      fileType?: string;
      mimetype?: string;
      /** Original upload filename; kept in step with `name` on rename. */
      filename?: string;
      /** Duplicate spelling of `filename`, written by the rename paths. */
      fileName?: string;
    };
    govQARequestId?: string;
    foiaXpressRequestId?: string;
    casepointRequestId?: string;
    nuixRequestId?: string;
    exterroRequestId?: string;
    hasAudioRedactions?: boolean;
    hasVideoRedactions?: boolean;
  };
  streams?: {
    protocol: string;
    uri: string;
  }[];
  folders: {
    // VE-21114: the folder's own id/name — needed to re-attach the leaf folder
    // that v2 excludes from `folderPath` (tdoFolderChain), for the soft-delete
    // relocate + Trash "Original Location".
    id?: FolderId;
    name?: string;
    folderPath: FolderPathItem[];
  }[];
}
/**
 * Does this output asset belong to the given file? Prefers the stamped
 * `parentTdoId`; the id-prefix branch is the legacy fallback for assets built
 * somewhere that doesn't stamp yet, and can go once every producer does.
 */
export const assetBelongsToFile = (
  asset: Pick<AssetFileType, 'id' | 'parentTdoId'>,
  tdoId: string
): boolean =>
  asset.parentTdoId ? asset.parentTdoId === tdoId : asset.id.startsWith(tdoId);

export interface SearchResultFolder {
  caseName: string;
  createdDateTime: string;
  // TODO: rename to folderId — this is now populated from the GraphQL id field
  // (see mapSearchFolderResults), not treeObjectId. The legacy name is misleading
  // and can cause incorrect routing/usage. Update all references when renaming.
  folderTreeObjectId: FolderId;
  contentTemplates?: ContentTemplateType[];
  fullFolderPath?: string;
}
export interface SearchResultFile {
  mediaId: number;
  filename: string;
  createdDateTime: string;
  fullFilePath: string;
  mimetype?: string;
  /** Canonical status identity (`redactionStatusKey ?? redactionStatus`). */
  redactionStatus?: string;
  /** Frozen human-readable status name for orphan display (VE-24546). */
  redactionStatusName?: string;
  // VE-25873 Unit 2 — the file's immediate parent folder/request, so the
  // breadcrumb in a search result can open that folder instead of the file.
  parentFolderId?: FolderId;
  parentIsCase?: boolean;
  // VE-25873 Unit 1 — populated only when the result was matched by a TDO-ID
  // lookup (a 10-digit search term). `fileId` is the TDO ID, shown after the
  // file name; `matchedByTdoId` gates that display.
  fileId?: number;
  matchedByTdoId?: boolean;
}

export interface SearchResultsCacheEntry {
  searchResults: (SearchResultFolder | SearchResultFile)[];
  searchMoreFilesOffset: number;
  searchMoreFoldersOffset: number;
  searchMoreFilesAvailable: boolean;
  searchMoreFoldersAvailable: boolean;
}

export interface FileFilters {
  fileRedacted: 'redacted' | 'not_redacted' | null;
  /** `'all'`, a well-known filter key, or a custom status label (VE-24546). */
  status: string;
  // 'status' orders files as the statuses appear on the status settings
  // page; 'redacted_first' puts files with redacted output above originals;
  // 'modified_date' is newest-modified first. The *_reverse / original_first
  // / modified_oldest values are their second-click inversions.
  sort:
    | 'a-z'
    | 'z-a'
    | 'newest'
    | 'oldest'
    | 'modified_date'
    | 'modified_oldest'
    | 'status'
    | 'status_reverse'
    | 'redacted_first'
    | 'original_first';
}

/**
 * Unassigned Media's sort. Narrower than `FileFilters['sort']` ON PURPOSE: this
 * column's files are SDOs paged from the server, which orders them itself, and
 * `structuredDataObjects` can order by exactly these two fields. The sorts that
 * are missing are missing because the SDO cannot answer them — see
 * `unassignedSortOrderBy`, which maps these onto the query.
 *
 * The values are spelled as `FileFilters['sort']` spells them so the one sort
 * control can label both columns from the same table.
 */
export type UnassignedSort = 'newest' | 'oldest' | 'modified_date';

// 'classic' = the original multi-column (Miller) cascade; 'details' =
// two-panel layout (folder panel + wide request panel) with the request's
// files as rows; 'grid' = the same two-panel layout with tiles.
export type FileViewMode = 'classic' | 'details' | 'grid';

// The last-used view mode survives reloads (unlike `filters`). Guarded
// try/catch: localStorage can throw (privacy modes) or not exist at all
// (this module can be pulled into the web-worker store bundle).
const FILE_VIEW_MODE_STORAGE_KEY = 'vtn-redact-casemedia-file-view-mode';

export const readStoredFileViewMode = (): FileViewMode => {
  try {
    const stored = localStorage.getItem(FILE_VIEW_MODE_STORAGE_KEY);
    // Default is Classic — Details/Grid only ever activate from an explicit
    // toggle choice. A stored legacy 'list' (the old two-way toggle, whose
    // list WAS the cascade) intentionally falls through to 'classic' too.
    return stored === 'grid' || stored === 'details' ? stored : 'classic';
  } catch {
    return 'classic';
  }
};

export const persistFileViewMode = (mode: FileViewMode): void => {
  try {
    localStorage.setItem(FILE_VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // Best-effort — the mode simply won't survive the reload.
  }
};

// VE-26943: Unassigned Media keeps its OWN view-mode preference, defaulting to
// Grid where every request defaults to Classic. That asymmetry is the point: a
// migrated gallery customer should land on something that looks like the gallery
// they know. One preference cannot hold two different defaults — and a user who
// deliberately switches to Details inside this folder must not be flipped back
// to Grid on re-entry, nor have their choice leak into real requests.
const UNASSIGNED_FILE_VIEW_MODE_STORAGE_KEY =
  'vtn-redact-casemedia-unassigned-file-view-mode';

export const readStoredUnassignedFileViewMode = (): FileViewMode => {
  try {
    const stored = localStorage.getItem(UNASSIGNED_FILE_VIEW_MODE_STORAGE_KEY);
    return stored === 'classic' || stored === 'details' ? stored : 'grid';
  } catch {
    return 'grid';
  }
};

export const persistUnassignedFileViewMode = (mode: FileViewMode): void => {
  try {
    localStorage.setItem(UNASSIGNED_FILE_VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // Best-effort — the mode simply won't survive the reload.
  }
};

// VE-26943: whether the folder sidebar is collapsed in the two-panel (Grid /
// Details) layouts — "wide screen mode". Two preferences, because the two
// contexts want opposite defaults and must not overwrite each other:
//
//   Unassigned Media  collapsed by default, which is what makes it read as a
//                     full-width gallery rather than one pane of a browser.
//   Any other request expanded by default — the folder panel is how people
//                     navigate, so it stays until they choose otherwise.
//
// Same reasoning as the view-mode split above: one key cannot hold two
// different defaults.
const UNASSIGNED_SIDEBAR_COLLAPSED_STORAGE_KEY =
  'vtn-redact-casemedia-unassigned-sidebar-collapsed';
const PANEL_SIDEBAR_COLLAPSED_STORAGE_KEY =
  'vtn-redact-casemedia-panel-sidebar-collapsed';

export const readStoredPanelSidebarCollapsed = (): boolean => {
  try {
    return localStorage.getItem(PANEL_SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const persistPanelSidebarCollapsed = (collapsed: boolean): void => {
  try {
    localStorage.setItem(
      PANEL_SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(collapsed)
    );
  } catch {
    // Best-effort — the choice simply won't survive the reload.
  }
};

export const readStoredUnassignedSidebarCollapsed = (): boolean => {
  try {
    return (
      localStorage.getItem(UNASSIGNED_SIDEBAR_COLLAPSED_STORAGE_KEY) !== 'false'
    );
  } catch {
    return true;
  }
};

// VE-26943: has the user closed the docked Unassigned Media panel? Auto-dock
// (opening the dock when the user leaves the folder for a request, so the
// drag-to-file workflow is continuous) must not fight a user who deliberately
// closed it, so the dismissal sticks until they dock again from the header.
const UNASSIGNED_DOCK_DISMISSED_STORAGE_KEY =
  'vtn-redact-casemedia-unassigned-dock-dismissed';

export const readStoredUnassignedDockDismissed = (): boolean => {
  try {
    return (
      localStorage.getItem(UNASSIGNED_DOCK_DISMISSED_STORAGE_KEY) === 'true'
    );
  } catch {
    return false;
  }
};

export const persistUnassignedDockDismissed = (dismissed: boolean): void => {
  try {
    localStorage.setItem(
      UNASSIGNED_DOCK_DISMISSED_STORAGE_KEY,
      String(dismissed)
    );
  } catch {
    // Best-effort — the dismissal simply won't survive the reload.
  }
};

export const persistUnassignedSidebarCollapsed = (collapsed: boolean): void => {
  try {
    localStorage.setItem(
      UNASSIGNED_SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(collapsed)
    );
  } catch {
    // Best-effort — the choice simply won't survive the reload.
  }
};

// Grid-mode panel split: the containing column's dragged width in px.
// Absent/invalid = never dragged, so the responsive clamp() default applies.
// The reader only sanity-checks (positive finite integer); the component
// clamps to its own layout bounds.
const GRID_PANEL_WIDTH_STORAGE_KEY = 'vtn-redact-casemedia-grid-panel-width';

export const readStoredGridPanelWidth = (): number | null => {
  try {
    const raw = localStorage.getItem(GRID_PANEL_WIDTH_STORAGE_KEY);
    const parsed = raw === null ? NaN : Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

export const persistGridPanelWidth = (widthPx: number): void => {
  try {
    localStorage.setItem(GRID_PANEL_WIDTH_STORAGE_KEY, String(widthPx));
  } catch {
    // Best-effort — the width simply won't survive the reload.
  }
};

// VE-26834: Details view's Name column width, once the user has dragged the
// divider between Name and Length. Absent = never dragged, so the column
// auto-fits the longest visible filename instead (double-clicking the divider
// clears the key and returns to that). Same contract as the panel width above:
// the reader only sanity-checks, the component clamps to its layout bounds.
//
// The case the width was dragged in is stored alongside it: the Details column
// REMOUNTS on a case switch (its Card is keyed by folder id), so this is the
// only way the new mount can tell "the user sized this for the case I'm showing"
// from "this width came from a different case" — which decides whether it
// survives names too long to fit (see shouldResetNameColumnWidthForCase).
const DETAILS_NAME_COLUMN_WIDTH_STORAGE_KEY =
  'vtn-redact-casemedia-details-name-column-width';

export interface StoredDetailsNameColumnWidth {
  widthPx: number;
  caseId: string | null;
}

export const readStoredDetailsNameColumnWidth =
  (): StoredDetailsNameColumnWidth | null => {
    try {
      const raw = localStorage.getItem(DETAILS_NAME_COLUMN_WIDTH_STORAGE_KEY);
      if (raw === null) {
        return null;
      }
      // Values written before the case id was added are a bare integer; read
      // them as a width with no owning case (so the first switch re-checks it).
      const stored: unknown = raw.startsWith('{')
        ? JSON.parse(raw)
        : { widthPx: Number.parseInt(raw, 10) };
      if (typeof stored !== 'object' || stored === null) {
        return null;
      }
      const { widthPx, caseId } = stored as {
        widthPx?: unknown;
        caseId?: unknown;
      };
      if (
        typeof widthPx !== 'number' ||
        !Number.isFinite(widthPx) ||
        widthPx <= 0
      ) {
        return null;
      }
      return {
        widthPx: Math.round(widthPx),
        caseId: typeof caseId === 'string' ? caseId : null,
      };
    } catch {
      return null;
    }
  };

export const persistDetailsNameColumnWidth = (
  widthPx: number,
  caseId?: string | null
): void => {
  try {
    localStorage.setItem(
      DETAILS_NAME_COLUMN_WIDTH_STORAGE_KEY,
      JSON.stringify({ widthPx, caseId: caseId ?? null })
    );
  } catch {
    // Best-effort — the width simply won't survive the reload.
  }
};

export const clearStoredDetailsNameColumnWidth = (): void => {
  try {
    localStorage.removeItem(DETAILS_NAME_COLUMN_WIDTH_STORAGE_KEY);
  } catch {
    // Best-effort — the column simply stays dragged until the next reload.
  }
};

// The deepest folder/request the user had open, restored on the next visit
// (CaseFolders re-selects it via fetchSelectedCaseOrFolderTree). Cleared when
// the user navigates back to root, and when a restore fails (e.g. the folder
// was deleted) so a stale id can't warn on every refresh.
//
// Keyed per org: folder ids are org-scoped, so the same browser profile
// switching orgs must not try to restore another org's folder (that only
// resolves to a not_found + snackbar). Each org gets its own bucket
// (`<prefix>:<orgId>`); switching orgs simply reads a different bucket and
// finds nothing to restore, defaulting to root. Callers pass the current
// org id (undefined before onboarding loads — then it's a no-op).
const LAST_OPEN_FOLDER_STORAGE_KEY_PREFIX =
  'vtn-redact-casemedia-last-open-folder';

const lastOpenFolderStorageKey = (orgId: string): string =>
  `${LAST_OPEN_FOLDER_STORAGE_KEY_PREFIX}:${orgId}`;

export interface StoredLastOpenFolder {
  id: string;
  isCase: boolean;
}

export const readStoredLastOpenFolder = (
  orgId: string | undefined
): StoredLastOpenFolder | null => {
  if (!orgId) {
    return null;
  }
  try {
    const raw = localStorage.getItem(lastOpenFolderStorageKey(orgId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<StoredLastOpenFolder>;
    return typeof parsed?.id === 'string' && parsed.id
      ? { id: parsed.id, isCase: !!parsed.isCase }
      : null;
  } catch {
    return null;
  }
};

export const persistLastOpenFolder = (
  orgId: string | undefined,
  value: StoredLastOpenFolder | null
): void => {
  if (!orgId) {
    return;
  }
  try {
    if (value) {
      localStorage.setItem(
        lastOpenFolderStorageKey(orgId),
        JSON.stringify(value)
      );
    } else {
      localStorage.removeItem(lastOpenFolderStorageKey(orgId));
    }
  } catch {
    // Best-effort — the last-open folder simply won't survive the reload.
  }
};

// VE-26118: is this folder in Trash (itself the system trash folder, or nested
// under it)? Moving a folder/request to Trash only reparents it, so it still
// resolves perfectly well through the API — a successful fetch is NOT proof the
// target is navigable, and the column tree must not restore into Trash.
//
// Checks the folder's own name as well as its folderPath: fetchCaseOrFolderTree
// returns a folderPath that includes the target itself, but other callers pass
// folders whose path holds ancestors only (cf. isTdoInTrash in mediaDetails,
// VE-21114), so covering both keeps this safe wherever it's reused.
export const isFolderInTrash = (
  folder: FolderStateType | null | undefined
): boolean =>
  folder?.name === SYSTEM_FOLDER_REDACT_TRASH ||
  !!folder?.folderPath?.some((f) => f?.name === SYSTEM_FOLDER_REDACT_TRASH);

export const defaultState: CaseMediaState = {
  searchText: '',
  searchDefaultOffset: 0,
  // TODO: optimal page size needs further analysis
  searchDefaultFolderLimit: 100,
  searchDefaultFileLimit: 100,
  searchMoreFilesOffset: 0,
  searchMoreFoldersOffset: 0,
  searchMoreFilesAvailable: false,
  searchMoreFoldersAvailable: false,
  isSearchMoreResults: false,
  isSearchInProgress: false,
  searchResults: [],
  searchResultsCache: {},
  searchScopeFolderId: null,
  searchScopeTreeObjectId: null,
  searchScopeFolderName: null,
  searchScopeIsCase: false,
  searchBarScopeMode: 'everything',
  isSearchDialogOpen: false,
  fetchingRootFolders: false,
  isNavToCaseMedia: false,
  isNavToCase: false,
  editorReturnTarget: null,
  folderViewTrip: null,
  rootFolder: {
    fetchingSubFolders: false,
    fetchSubFolderLimit: 0,
    fetchSubFolderOffset: 0,
    fetchMoreAvailable: false,
  },
  rootFolderId: '',
  rootFolderSmartSelection: false,
  selectedFolders: [],
  selectedVirtualFolder: {},
  defaultFetchMoreLimit: 30,
  defaultFetchMoreOffset: 0,
  deletingFileInProgress: false,
  isShowLegacyMedias: false,
  isFetchingLegacyMedias: false,
  LegacyMedias: [],
  legacyTdoDetails: {},
  hasFetchedLegacyMedias: false,
  legacyTdoDetailAttempted: {},
  fetchMoreLegacyMediasOffset: 0,
  fetchMoreLegacyMediasAvailable: false,
  allExpandedFolders: [],
  filters: {
    fileRedacted: null,
    status: 'all',
    // VE-32420: alphabetical, the convention every file browser uses and the
    // only order in which a file's position is guessable.
    //
    // It used to be 'oldest', and NOT for preference: files were lazy-loaded a
    // page at a time, so an A-Z default merged each arriving batch into the
    // middle of the list and shifted rows the user was looking at. Oldest-first
    // was stable only because new pages happened to append at the bottom.
    //
    // The roster removed that constraint — the whole request is indexed before
    // anything is sorted, so no arriving page can reorder what is on screen.
    // Do not restore 'oldest' on the old reasoning; it no longer applies.
    sort: 'a-z',
  },
  fileViewMode: readStoredFileViewMode(),
  unassignedFileViewMode: readStoredUnassignedFileViewMode(),
  // Newest first: the only key here that is immutable, so the only one whose
  // paging is stable (see `unassignedSortOrderBy`). This is also what the
  // docked panel always shows, since leaving the column resets to it. It was
  // most-recently-MODIFIED back when the query hardcoded that ordering.
  unassignedSort: 'newest',
  isUnassignedSidebarCollapsed: readStoredUnassignedSidebarCollapsed(),
  isPanelSidebarCollapsed: readStoredPanelSidebarCollapsed(),
  folderTreeFetchState: 'idle',
  caseContentsLoadedAt: {},
  caseContentsRows: {},
  subFoldersLoadedAt: {},
  subFoldersRows: {},
  requestRoster: {},
  lastActiveFolderViewFile: null,
  openedFolderViewTdoIds: [],
  lastOpenedFolderViewFileByCase: {},
  folderPermissions: {},
  multiSelectedFiles: [],
  multiSelectCaseId: null,
  multiSelectAnchorId: null,
  pendingRemovalFileIds: [],
  pendingTrashFolderIds: [],
  batchOperation: null,
};

export enum VirtualFolderNames {
  INPUT = 'Input',
  OUTPUT = 'Output',
}
